import { BondingFormModal } from '@/components/admin/BondingFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { LlmBonding, LlmBondingProbeMemberResult, LlmEndpoint } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, MoreHorizontal, Plus, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatRelative } from './EndpointDetail';

type ModalState = { kind: 'create' } | { kind: 'edit'; bonding: LlmBonding } | null;

export function AdminLlmBondings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<{
    id: string;
    members: LlmBondingProbeMemberResult[];
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llm-bondings'],
    queryFn: () => api.adminLlmBondingList(),
    refetchInterval: 30_000,
    refetchOnMount: 'always',
  });

  // 페이지 진입 즉시 + 30초마다 본딩에 속한 모든 멤버 엔드포인트 probe → DB 갱신.
  // 본딩의 상태 표시는 멤버 엔드포인트들의 last probe 를 집계해 derived.
  useQuery({
    queryKey: ['admin', 'llm-bondings', 'probe-all'],
    queryFn: async () => {
      await api.adminLlmBondingProbeAll();
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'], exact: true });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'], exact: true });
      return Date.now();
    },
    refetchInterval: 30_000,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // 본딩별 멤버 엔드포인트 상태를 가져오기 위해 엔드포인트 리스트도 같이 캐시 (이름 클릭 시 detail 안 가도 멤버 last probe 를 보여주려면 필요).
  const endpointsQuery = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    refetchInterval: 30_000,
  });

  const updateMut = useMutation({
    mutationFn: ({
      id,
      body,
    }: { id: string; body: Parameters<typeof api.adminLlmBondingUpdate>[1] }) =>
      api.adminLlmBondingUpdate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminLlmBondingDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const probeMut = useMutation({
    mutationFn: (id: string) => api.adminLlmBondingProbe(id).then((r) => ({ ...r, id })),
    onSuccess: (r) => setProbeResult({ id: r.id, members: r.members }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '연결 테스트 실패.');
    },
  });

  const bondings = data?.bondings ?? [];
  const endpointById = new Map((endpointsQuery.data?.endpoints ?? []).map((e) => [e.id, e]));

  return (
    <PageShell
      header={
        <PageHeader
          title="LLM 본딩"
          count={bondings.length}
          description="여러 엔드포인트를 가상 LLM 으로 묶습니다. 랜덤 균등 분배 또는 우선순위 라우팅(상위부터 사용, 불가 시 다음)을 선택할 수 있습니다. 사용자는 본딩 이름만 보게 됩니다."
          actions={
            <Button onClick={() => setModal({ kind: 'create' })}>
              <Plus className="size-4" />
              본딩 추가
            </Button>
          }
        />
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="ml-3 text-xs underline"
          >
            닫기
          </button>
        </div>
      ) : null}

      {probeResult ? (
        <div className="mb-4 rounded-md border border-border bg-surface px-3 py-2 text-sm">
          <div className="mb-1 font-medium">
            멤버 연결 테스트 결과 ({probeResult.members.filter((m) => m.ok).length}/
            {probeResult.members.length} 성공)
          </div>
          <ul className="space-y-0.5">
            {probeResult.members.map((m) => (
              <li key={m.memberId} className="flex items-center gap-2 text-[12px]">
                <span
                  className={cn(
                    'inline-flex h-4 w-4 items-center justify-center rounded-sm text-[9px]',
                    m.ok ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive',
                  )}
                >
                  {m.ok ? '✓' : '×'}
                </span>
                <span className="font-medium">{m.name}</span>
                <span className="text-foreground-muted">
                  {m.ok ? `${m.models.length}개 모델` : (m.error ?? '실패')}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setProbeResult(null)}
            className="mt-1 text-xs underline text-foreground-muted"
          >
            닫기
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '본딩 목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>이름</Th>
                <Th>멤버</Th>
                <Th>릴레이 alias</Th>
                <Th>상태</Th>
                <Th>플래그</Th>
                <Th align="right">액션</Th>
              </tr>
            </thead>
            <tbody>
              {bondings.map((b) => (
                <Row
                  key={b.id}
                  bonding={b}
                  memberStatus={summarizeMembers(b, endpointById)}
                  onOpen={() => navigate({ to: '/admin/llm/bondings/$id', params: { id: b.id } })}
                  onEdit={() => setModal({ kind: 'edit', bonding: b })}
                  onProbe={() => probeMut.mutate(b.id)}
                  onToggleDefault={() =>
                    updateMut.mutate({ id: b.id, body: { isDefault: !b.isDefault } })
                  }
                  onToggleEnabled={() =>
                    updateMut.mutate({ id: b.id, body: { isEnabled: !b.isEnabled } })
                  }
                  onToggleEnabledForUsers={() =>
                    updateMut.mutate({
                      id: b.id,
                      body: { isEnabledForUsers: !b.isEnabledForUsers },
                    })
                  }
                  onDelete={() => {
                    if (window.confirm(`본딩 "${b.displayName ?? b.name}" 을 삭제할까요?`)) {
                      deleteMut.mutate(b.id);
                    }
                  }}
                />
              ))}
              {bondings.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-foreground-muted py-10">
                    등록된 본딩이 없습니다.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <BondingFormModal open={!!modal} mode={modal} onClose={() => setModal(null)} />
    </PageShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

type MemberStatus = {
  total: number;
  okCount: number;
  hasAny: boolean;
  latestProbedAt: string | null;
};

function summarizeMembers(b: LlmBonding, endpointById: Map<string, LlmEndpoint>): MemberStatus {
  const total = b.memberIds.length;
  let okCount = 0;
  let hasAny = false;
  let latestProbedAt: string | null = null;
  for (const id of b.memberIds) {
    const ep = endpointById.get(id);
    if (!ep || ep.lastProbedAt === null) continue;
    hasAny = true;
    if (ep.lastProbeOk === true) okCount += 1;
    if (latestProbedAt === null || new Date(ep.lastProbedAt) > new Date(latestProbedAt)) {
      latestProbedAt = ep.lastProbedAt;
    }
  }
  return { total, okCount, hasAny, latestProbedAt };
}

function Row({
  bonding,
  memberStatus,
  onOpen,
  onEdit,
  onProbe,
  onToggleDefault,
  onToggleEnabled,
  onToggleEnabledForUsers,
  onDelete,
}: {
  bonding: LlmBonding;
  memberStatus: MemberStatus;
  onOpen: () => void;
  onEdit: () => void;
  onProbe: () => void;
  onToggleDefault: () => void;
  onToggleEnabled: () => void;
  onToggleEnabledForUsers: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-t border-border hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <button type="button" onClick={onOpen} className="text-left hover:underline font-medium">
          {bonding.displayName ?? bonding.name}
        </button>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-muted">
          <span className="inline-flex items-center rounded-sm bg-surface-muted px-1.5 h-4 uppercase tracking-wider">
            BONDING
          </span>
          <span className="font-mono">{bonding.name}</span>
        </div>
        {bonding.description ? (
          <div className="mt-0.5 text-[11px] text-foreground-muted">{bonding.description}</div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs font-mono">{bonding.memberIds.length}개</span>
        <div className="mt-0.5">
          {bonding.routingStrategy === 'priority' ? (
            <Badge tone="accent">우선순위</Badge>
          ) : (
            <Badge tone="mute">랜덤</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs">
        {bonding.relayAlias ? bonding.relayAlias : <span className="text-foreground-muted">—</span>}
      </td>
      <td className="px-4 py-3">
        <MemberStatusInline status={memberStatus} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {bonding.isDefault ? <Badge tone="accent">기본</Badge> : null}
          {bonding.isEnabled ? (
            bonding.isEnabledForUsers ? (
              <Badge tone="ok">사용자 노출</Badge>
            ) : (
              <Badge tone="mute">숨김</Badge>
            )
          ) : (
            <Badge tone="warn">비활성</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <RowMenu
          isDefault={bonding.isDefault}
          isEnabled={bonding.isEnabled}
          isEnabledForUsers={bonding.isEnabledForUsers}
          onEdit={onEdit}
          onProbe={onProbe}
          onToggleDefault={onToggleDefault}
          onToggleEnabled={onToggleEnabled}
          onToggleEnabledForUsers={onToggleEnabledForUsers}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

function MemberStatusInline({ status }: { status: MemberStatus }) {
  if (!status.hasAny) {
    return <span className="text-xs text-foreground-muted">—</span>;
  }
  const allOk = status.okCount === status.total;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {allOk ? (
        <CheckCircle2 className="size-3.5 text-success" />
      ) : (
        <XCircle className="size-3.5 text-destructive" />
      )}
      <span className={allOk ? '' : 'text-destructive'}>
        {status.okCount}/{status.total}
      </span>
      <span className="text-foreground-muted">{formatRelative(status.latestProbedAt)}</span>
    </span>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'accent' | 'ok' | 'mute' | 'warn';
}) {
  const cls = {
    accent: 'bg-accent/15 text-accent',
    ok: 'bg-success/15 text-success',
    mute: 'bg-surface-muted text-foreground-muted',
    warn: 'bg-destructive/15 text-destructive',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        cls,
      )}
    >
      {children}
    </span>
  );
}

function RowMenu({
  isDefault,
  isEnabled,
  isEnabledForUsers,
  onEdit,
  onProbe,
  onToggleDefault,
  onToggleEnabled,
  onToggleEnabledForUsers,
  onDelete,
}: {
  isDefault: boolean;
  isEnabled: boolean;
  isEnabledForUsers: boolean;
  onEdit: () => void;
  onProbe: () => void;
  onToggleDefault: () => void;
  onToggleEnabled: () => void;
  onToggleEnabledForUsers: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="w-56 z-50 rounded-md border border-border bg-surface text-foreground shadow-lg py-1 text-sm text-left"
            >
              <MenuItem
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                편집
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onProbe();
                }}
              >
                멤버 연결 테스트
              </MenuItem>
              <MenuItem
                disabled={isDefault}
                onClick={() => {
                  close();
                  onToggleDefault();
                }}
              >
                {isDefault ? '기본 모델 (현재)' : '기본 모델로 지정'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onToggleEnabled();
                }}
              >
                {isEnabled ? '본딩 비활성화' : '본딩 활성화'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onToggleEnabledForUsers();
                }}
              >
                {isEnabledForUsers ? '사용자 노출 해제' : '사용자에게 노출'}
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                danger
                onClick={() => {
                  close();
                  onDelete();
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      {children}
    </button>
  );
}
